import React from 'react';
import { Row, Col, Card, Typography, Button, Tag, Timeline, Divider } from 'antd';
import { 
  BookOutlined, 
  LinkOutlined, 
  CalendarOutlined,
  FileTextOutlined,
  TrophyOutlined,
  GlobalOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const Publications = () => {
  const publications = [
    {
      id: 1,
      title: 'Evaluating the Vulnerability of Deep Learning Models in Medical Imaging to Adversarial Perturbations',
      journal: 'International Journal of Computer Applications (IJCA)',
      date: '2025',
      volume: 'Volume 187, Number 55',
      pages: 'Pages 46-60',
      doi: '10.5120/ijca2025925973',
      url: 'https://www.ijcaonline.org/archives/volume187/number55/evaluating-the-vulnerability-of-deep-learning-models-in-medical-imaging-to-adversarial-perturbations/',
      type: 'Research Paper',
      status: 'Published',
      authors: 'Hamuza Senyonga, Charity Mahwire, Thelma Chimusoro, Enock Katenda',
      abstract: 'Deep learning has revolutionized medical imaging, but it is vulnerable to adversarial attacks, which are deemed dangerous to clinical use. This paper compares the strength of convolutional neural networks (CNNs) and Vision Transformers (ViTs) trained on the ChestX-ray14 dataset at NIH to detect pneumonia. Both models showed high baseline accuracy (>90%) even when attacked by Fast Gradient Sign Method (FGSM), Projected Gradient Descent (PGD), DeepFool, and Carlini-Wagner, although PGD and CW were the most disruptive. The evaluation of defense strategies was also performed, involving adversarial training, input preprocessing, ensemble modeling, and adversarial detection. Adversarial training provided the best protection, at the cost of lower clean-data accuracy. The findings reveal the necessity of layered defense practices and ethical and regulatory issues related to trust, liability, and patient safety.',
      keywords: ['Adversarial Attacks', 'Deep Learning', 'Medical Imaging', 'CNNs', 'Vision Transformers', 'Healthcare AI', 'Patient Safety', 'Cybersecurity in Medicine'],
      citation: 'Senyonga, H., Mahwire, C., Chimusoro, T., & Katenda, E. (2025). Evaluating the Vulnerability of Deep Learning Models in Medical Imaging to Adversarial Perturbations. International Journal of Computer Applications (IJCA), 187(55), 46-60. DOI: 10.5120/ijca2025925973.'
    },
    {
      id: 2,
      title: 'An Analytics-Driven Metrics-Based Framework for Optimising Security and Performance in Hybrid Enterprise Zero Trust Deployments',
      journal: 'International Journal of Computer Applications (IJCA)',
      date: '2025',
      volume: 'Volume 187, Number 16',
      url: 'https://www.ijcaonline.org/archives/volume187/number16/an-analytics-driven-metrics-based-framework-for-optimising-security-and-performance-in-hybrid-enterprise-zero-trust-deployments/',
      type: 'Research Paper',
      status: 'Published',
      abstract: 'This paper presents a comprehensive framework for optimizing security and performance in hybrid enterprise Zero Trust deployments using analytics-driven metrics. The research addresses the growing need for robust security architectures in modern enterprise environments.',
      keywords: ['Zero Trust', 'Enterprise Security', 'Performance Optimization', 'Analytics', 'Hybrid Cloud'],
      citation: 'Katenda, E. (2025). An Analytics-Driven Metrics-Based Framework for Optimising Security and Performance in Hybrid Enterprise Zero Trust Deployments. International Journal of Computer Applications (IJCA), Volume 187, Number 16.'
    },
    {
      id: 3,
      title: 'Enhancing Enterprise Security through Zero Trust Architectures: A Metrics-Driven Approach',
      journal: 'Elementary Education Online (ESTJ Journal)',
      date: '2025',
      volume: 'ESTJ Journal',
      url: 'https://www.fepbl.com/index.php/estj/article/view/1971',
      type: 'Research Paper',
      status: 'Published',
      abstract: 'This study explores the implementation of Zero Trust security architectures in enterprise environments, focusing on metrics-driven approaches to enhance overall security posture and operational efficiency.',
      keywords: ['Zero Trust Architecture', 'Enterprise Security', 'Metrics', 'Security Frameworks'],
      citation: 'Katenda, E. (2025). Enhancing Enterprise Security through Zero Trust Architectures: A Metrics-Driven Approach. Elementary Education Online (ESTJ Journal).'
    }
  ];

  const upcomingPublications = [
    {
      title: 'Machine Learning Applications in Financial System Security',
      journal: 'Journal of Financial Technology',
      expectedDate: '2025',
      status: 'Under Review',
      type: 'Research Paper'
    },
    {
      title: 'Scalable Microservices Architecture for Enterprise Applications',
      journal: 'IEEE Software Engineering Transactions',
      expectedDate: '2025',
      status: 'In Preparation',
      type: 'Research Paper'
    }
  ];

  const researchAreas = [
    {
      area: 'Healthcare AI & Medical Imaging',
      description: 'Research on deep learning models for medical imaging, adversarial robustness in healthcare AI, and patient safety in clinical applications.',
      publications: 1
    },
    {
      area: 'Cybersecurity & Zero Trust Architecture',
      description: 'Research focus on enterprise security frameworks, Zero Trust implementations, and security performance optimization.',
      publications: 2
    },
    {
      area: 'Machine Learning in Security',
      description: 'Exploring AI and ML applications in cybersecurity, fraud detection, and automated threat response.',
      publications: 1
    },
    {
      area: 'Software Engineering & System Architecture',
      description: 'Studies on scalable system design, microservices architecture, and enterprise application development.',
      publications: 1
    }
  ];

  return (
    <section id="publications" className="section section-light">
      <div className="container">
        <div className="section-header">
          <Title level={2} className="section-title">Research & Publications</Title>
          <Paragraph className="section-subtitle">
            My academic research contributions and published works in computer science and software engineering
          </Paragraph>
          
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <Button
              type="primary"
              size="large"
              icon={<BookOutlined />}
              href="https://scholar.google.com/citations?user=FMejVSsAAAAJ&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: '#1890ff',
                borderColor: '#1890ff',
                height: '48px',
                padding: '0 32px',
                fontSize: '16px'
              }}
            >
              View Google Scholar Profile
            </Button>
          </div>
        </div>

        {/* Published Papers */}
        <div style={{ marginBottom: '60px' }}>
          <Title level={3} style={{ marginBottom: '32px', color: '#1890ff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileTextOutlined />
            Published Papers
          </Title>
          
          <Row gutter={[24, 24]}>
            {publications.map((pub, index) => (
              <Col key={pub.id} xs={24} lg={12}>
                <Card
                  className="fade-in-up"
                  style={{ 
                    height: '100%',
                    animationDelay: `${index * 0.1}s`
                  }}
                  title={
                    <div>
                      <Title level={4} style={{ margin: 0, marginBottom: '8px', fontSize: '16px', lineHeight: 1.4 }}>
                        {pub.title}
                      </Title>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Tag color="blue">{pub.type}</Tag>
                        <Tag color="green">{pub.status}</Tag>
                        <Tag color="orange">{pub.date}</Tag>
                      </div>
                    </div>
                  }
                  extra={
                    <Button
                      type="link"
                      icon={<LinkOutlined />}
                      href={pub.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ padding: 0 }}
                    >
                      Read Paper
                    </Button>
                  }
                >
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ color: '#1890ff' }}>{pub.journal}</Text>
                    <br />
                    <Text type="secondary">{pub.volume}</Text>
                    {pub.pages && (
                      <>
                        <br />
                        <Text type="secondary">{pub.pages}</Text>
                      </>
                    )}
                    {pub.doi && (
                      <>
                        <br />
                        <Text type="secondary">DOI: {pub.doi}</Text>
                      </>
                    )}
                    {pub.authors && (
                      <>
                        <br />
                        <Text type="secondary" style={{ fontSize: '13px', fontStyle: 'italic' }}>
                          Authors: {pub.authors}
                        </Text>
                      </>
                    )}
                  </div>

                  <Paragraph style={{ marginBottom: '16px', fontSize: '14px' }}>
                    {pub.abstract}
                  </Paragraph>

                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '12px' }}>Keywords: </Text>
                    <div style={{ marginTop: '4px' }}>
                      {pub.keywords.map((keyword, idx) => (
                        <Tag key={idx} size="small" style={{ marginBottom: '4px' }}>
                          {keyword}
                        </Tag>
                      ))}
                    </div>
                  </div>

                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '12px', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <Text strong style={{ fontSize: '12px', color: '#666' }}>Citation:</Text>
                    <br />
                    <Text style={{ fontSize: '11px', fontStyle: 'italic' }}>
                      {pub.citation}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Research Areas */}
        <div style={{ marginBottom: '60px' }}>
          <Title level={3} style={{ marginBottom: '32px', color: '#1890ff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BookOutlined />
            Research Areas
          </Title>
          
          <Row gutter={[24, 24]}>
            {researchAreas.map((area, index) => (
              <Col key={index} xs={24} md={8}>
                <Card
                  className="fade-in-up"
                  style={{ 
                    height: '100%',
                    animationDelay: `${index * 0.1}s`
                  }}
                  hoverable
                >
                  <Title level={4} style={{ color: '#1890ff', marginBottom: '12px' }}>
                    {area.area}
                  </Title>
                  
                  <Paragraph style={{ marginBottom: '16px' }}>
                    {area.description}
                  </Paragraph>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrophyOutlined style={{ color: '#52c41a' }} />
                    <Text strong>{area.publications} Publication{area.publications !== 1 ? 's' : ''}</Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Upcoming Publications */}
        <div>
          <Title level={3} style={{ marginBottom: '32px', color: '#1890ff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <CalendarOutlined />
            Upcoming Publications
          </Title>
          
          <Timeline>
            {upcomingPublications.map((pub, index) => (
              <Timeline.Item
                key={index}
                color="blue"
                dot={<GlobalOutlined />}
              >
                <Card size="small" style={{ marginBottom: '16px' }}>
                  <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                    {pub.title}
                  </Title>
                  
                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ color: '#1890ff' }}>{pub.journal}</Text>
                    <br />
                    <Text type="secondary">Expected: {pub.expectedDate}</Text>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Tag color="blue">{pub.type}</Tag>
                    <Tag color="orange">{pub.status}</Tag>
                  </div>
                </Card>
              </Timeline.Item>
            ))}
          </Timeline>
        </div>

        {/* Research Metrics */}
        <div style={{ marginTop: '60px' }}>
          <Card style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', textAlign: 'center' }}>
            <Title level={3} style={{ color: 'white', marginBottom: '24px' }}>
              Research Impact
            </Title>
            
            <Row gutter={[32, 32]}>
              <Col xs={24} sm={8}>
                <div>
                  <Title level={2} style={{ color: 'white', margin: 0 }}>3</Title>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Published Papers</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <Title level={2} style={{ color: 'white', margin: 0 }}>4</Title>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Research Areas</Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div>
                  <Title level={2} style={{ color: 'white', margin: 0 }}>2</Title>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Upcoming Papers</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Publications;
